import React, { useEffect, useMemo, useState } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { fixedAssetsAPI } from '../../lib/api';
import AssetWorkflowList from '../../components/assets/AssetWorkflowList';
import AssetStatusBadge from '../../components/assets/AssetStatusBadge';
import AssetPageHeader from '../../components/assets/AssetPageHeader';

const today = () => new Date().toISOString().slice(0, 10);
const rowsFrom = (payload) => Array.isArray(payload) ? payload : payload?.items || [];
const money = (value) => `LKR ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const initialForm = () => ({ asset_id: '', supplier_invoice_id: '', capitalization_date: today(), available_for_use_date: today(), useful_life_months: 60, residual_value: 0, depreciation_method: 'straight_line', eligible_cost: '' });

export default function AssetCapitalizations() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [open, setOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(initialForm());
  const [saving, setSaving] = useState(false);

  const categoryNames = useMemo(() => Object.fromEntries(categories.map((row) => [row.id, row.name])), [categories]);
  const selectedAsset = assets.find((row) => row.id === form.asset_id);

  const loadReferences = async () => {
    try {
      const [assetResponse, invoiceResponse, categoryResponse] = await Promise.all([
        fixedAssetsAPI.assets.list({ page: 1, page_size: 200, capitalization_status: 'not_capitalized' }),
        fixedAssetsAPI.acquisitions.invoices({ page: 1, page_size: 200, posting_status: 'posted' }),
        fixedAssetsAPI.categories.list(),
      ]);
      setAssets(rowsFrom(assetResponse.data).filter((asset) => ['approved', 'received', 'pending_capitalization'].includes(asset.status)));
      setInvoices(rowsFrom(invoiceResponse.data).filter((invoice) => invoice.posting_status === 'posted'));
      setCategories(categoryResponse.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load capitalization references');
    }
  };

  useEffect(() => { loadReferences(); }, [refreshKey]);

  const chooseAsset = (assetId) => {
    const asset = assets.find((row) => row.id === assetId);
    setForm((current) => ({
      ...current,
      asset_id: assetId,
      useful_life_months: Number(asset?.useful_life_months || 60),
      residual_value: Number(asset?.residual_value || 0),
      depreciation_method: asset?.method || 'straight_line',
      eligible_cost: asset?.purchase_price ? String(asset.purchase_price) : '',
      supplier_invoice_id: invoices.find((invoice) => invoice.asset_purchase_order_id && invoice.asset_purchase_order_id === asset?.asset_purchase_order_id)?.id || current.supplier_invoice_id,
    }));
  };

  const create = async () => {
    if (!form.asset_id) { toast.error('Select the received asset'); return; }
    if (!form.supplier_invoice_id) { toast.error('Select the posted supplier invoice'); return; }
    setSaving(true);
    try {
      const payload = {
        asset_id: form.asset_id,
        supplier_invoice_ids: [form.supplier_invoice_id],
        receipt_ids: selectedAsset?.asset_receipt_id ? [selectedAsset.asset_receipt_id] : [],
        capitalization_date: form.capitalization_date,
        available_for_use_date: form.available_for_use_date,
        eligible_cost_components: form.eligible_cost === '' ? {} : { source_acquisition_cost: Number(form.eligible_cost) },
        excluded_cost_components: {},
        useful_life_months: Number(form.useful_life_months || 60),
        residual_value: Number(form.residual_value || 0),
        depreciation_method: form.depreciation_method,
        depreciation_start_date: form.available_for_use_date,
      };
      await fixedAssetsAPI.capitalizations.create(payload);
      toast.success('Capitalization created. Submit, approve and post it from the list.');
      setOpen(false); setForm(initialForm()); setRefreshKey((value) => value + 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create capitalization');
    } finally {
      setSaving(false);
    }
  };

  const action = async (id, type) => {
    try {
      await fixedAssetsAPI.capitalizations[type](id);
      toast.success(`Capitalization ${type} complete`);
      setRefreshKey((value) => value + 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Could not ${type} capitalization`);
    }
  };

  const columns = [
    { key: 'capitalization_number', label: 'Number' },
    { key: 'asset_id', label: 'Asset', render: (row) => assets.find((asset) => asset.id === row.asset_id)?.asset_number || row.asset_id },
    { key: 'capitalization_date', label: 'Date' },
    { key: 'total_capitalized_cost', label: 'Capitalized cost', render: (row) => money(row.total_capitalized_cost) },
    { key: 'approval_status', label: 'Approval', status: true },
    { key: 'posting_status', label: 'Posting', status: true },
    { key: 'actions', label: 'Actions', render: (row) => <div className="flex flex-wrap gap-1">
      {row.posting_status === 'draft' && <Button size="sm" variant="outline" onClick={() => action(row.id, 'submit')}>Submit</Button>}
      {row.posting_status === 'pending_approval' && <Button size="sm" variant="outline" onClick={() => action(row.id, 'approve')}>Approve</Button>}
      {row.posting_status === 'approved' && <Button size="sm" onClick={() => action(row.id, 'post')}>Post</Button>}
      {row.posting_status === 'posted' && <AssetStatusBadge status="posted" />}
    </div> },
  ];

  return (
    <div className="space-y-6">
      <AssetPageHeader
        title="Asset Capitalization"
        description="Create the capitalization from a received asset and posted supplier invoice. Posting transfers the eligible cost from Fixed Asset Clearing into the category asset account and generates the depreciation schedule."
        actions={<>
          <Button variant="outline" onClick={() => setRefreshKey((value) => value + 1)}><RefreshCw className="mr-2 h-4 w-4" />Refresh</Button>
          <Button onClick={() => { loadReferences(); setOpen(true); }}><Plus className="mr-2 h-4 w-4" />New Capitalization</Button>
        </>}
      />

      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
        <strong>Required sequence:</strong> receive the Asset PO → create and Post Invoice → record payment or director funding → create capitalization → Submit → Approve → Post.
      </div>

      <AssetWorkflowList key={refreshKey} title="Capitalization Records" description="Draft, approval and posting status for every capitalization." load={() => fixedAssetsAPI.capitalizations.list({ page_size: 200 })} columns={columns} hideHeader />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Create Asset Capitalization</DialogTitle></DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2"><Label>Received asset</Label><Select value={form.asset_id} onValueChange={chooseAsset}><SelectTrigger className="mt-1"><SelectValue placeholder="Select an asset" /></SelectTrigger><SelectContent>{assets.map((asset) => <SelectItem key={asset.id} value={asset.id}>{asset.asset_number} — {asset.name} ({categoryNames[asset.category_id] || 'Category'})</SelectItem>)}</SelectContent></Select></div>
            <div className="md:col-span-2"><Label>Posted supplier invoice</Label><Select value={form.supplier_invoice_id} onValueChange={(value) => setForm({ ...form, supplier_invoice_id: value })}><SelectTrigger className="mt-1"><SelectValue placeholder="Select posted invoice" /></SelectTrigger><SelectContent>{invoices.map((invoice) => <SelectItem key={invoice.id} value={invoice.id}>{invoice.internal_invoice_number} — {invoice.invoice_number} — {money(invoice.total)}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Capitalization date</Label><Input className="mt-1" type="date" value={form.capitalization_date} onChange={(event) => setForm({ ...form, capitalization_date: event.target.value })} /></div>
            <div><Label>Available-for-use date</Label><Input className="mt-1" type="date" value={form.available_for_use_date} onChange={(event) => setForm({ ...form, available_for_use_date: event.target.value })} /></div>
            <div><Label>Eligible cost</Label><Input className="mt-1" type="number" min="0" value={form.eligible_cost} onChange={(event) => setForm({ ...form, eligible_cost: event.target.value })} placeholder="Auto from receipt/invoice" /><p className="mt-1 text-xs text-slate-500">Leave blank to derive from the asset receipt or posted invoice.</p></div>
            <div><Label>Residual value</Label><Input className="mt-1" type="number" min="0" value={form.residual_value} onChange={(event) => setForm({ ...form, residual_value: Number(event.target.value) })} /></div>
            <div><Label>Useful life (months)</Label><Input className="mt-1" type="number" min="1" value={form.useful_life_months} onChange={(event) => setForm({ ...form, useful_life_months: Number(event.target.value) })} /></div>
            <div><Label>Depreciation method</Label><Select value={form.depreciation_method} onValueChange={(value) => setForm({ ...form, depreciation_method: value })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{['straight_line', 'reducing_balance', 'double_declining_balance', 'units_of_production', 'manual', 'none'].map((value) => <SelectItem key={value} value={value}>{value.replaceAll('_', ' ')}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} disabled={saving}>{saving ? 'Creating…' : 'Create Capitalization'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
